import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { ViewEventData } from '@databases/analytics/src/types';
import { useDialog } from '@databases/dialog';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { DatabaseChangeType } from '@databases/types/redux';
import React from 'react';
import { useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { messages as viewSettingsMessages } from '../i18n';

import useView from './useView';
import useViewPanel from './useViewPanel';

const useDeleteView = (contentId: string) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const client = useAnalytics();

  const { closeViewSettingsPanel } = useViewPanel();
  const { view, setViewId, resolvedView } = useView();
  const { showDialog } = useDialog();

  const intl = useIntl();
  const { formatMessage } = intl;

  return useCallback(
    (viewId: string, source: ViewEventData['source'], onCloseAdditional?: () => void) => {
      showDialog('ConfirmDialog', {
        heading: formatMessage(messages['toolbar-views.delete-view-heading']),
        body: <p>{formatMessage(messages['toolbar-views.delete-view-text'])}</p>,
        yesLabel: formatMessage(
          viewSettingsMessages['view-actions.delete-view-delete-button']
        ),
        noLabel: formatMessage(messages['toolbar-views.delete-view-cancel-button']),
        appearance: 'danger',
        onClose: (result) => {
          if (result) {
            void client.sendViewDeletedEvent({
              contentId,
              databaseContainer: getDatabaseContainerType(),
              source: source,
              viewId: viewId,
            });

            setViewId(resolvedView.id);
            if (view.id === viewId) {
              closeViewSettingsPanel();
            }
            dispatch(
              addChangeToDraft(contentId, 'main', {
                type: DatabaseChangeType.DELETE_SAVED_VIEW,
                viewId: viewId,
              })
            );

            onCloseAdditional && onCloseAdditional();
          }
        },
      });
    },
    [
      showDialog,
      formatMessage,
      client,
      contentId,
      setViewId,
      resolvedView.id,
      view.id,
      dispatch,
      closeViewSettingsPanel,
    ]
  );
};

export default useDeleteView;
